import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostErrorType } from 'api/clients/ScheduledPostsClient'
import { capitalize } from 'lodash'

const PLATFORM_ERROR_PATTERNS: Partial<Record<ExternalIntegrationEnum, Array<{ pattern: RegExp; message: string }>>> = {
  [ExternalIntegrationEnum.Shopify]: [
    {
      pattern: /handle has already been taken/i,
      message: 'Post URL already exists. Update the URL slug in the metadata panel and try again.',
    },
  ],
}

const parsePlatformSpecificError = (errorMessage: string, platform?: ExternalIntegrationEnum): string | null => {
  if (!platform || !errorMessage) return null

  const patterns = PLATFORM_ERROR_PATTERNS[platform]
  if (!patterns) return null

  for (const { pattern, message } of patterns) {
    if (pattern.test(errorMessage)) {
      return message
    }
  }

  return null
}

export const formatPostError = (
  errorType?: ScheduledPostErrorType,
  platform?: ExternalIntegrationEnum,
  errorMessage?: string,
): string => {
  // First, check for platform-specific error patterns
  if (errorMessage) {
    const platformSpecificMessage = parsePlatformSpecificError(errorMessage, platform)
    if (platformSpecificMessage) {
      return platformSpecificMessage
    }
  }

  // Fall back to error type messages
  const platformName = platform ? capitalize(platform) : 'The platform'

  switch (errorType) {
    case ScheduledPostErrorType.AuthenticationError:
    case ScheduledPostErrorType.AuthorizationError:
      return `${platformName} connection expired. Please refresh your integration.`
    case ScheduledPostErrorType.ValidationError:
      return `${platformName} rejected the post content. Please check your content and try again.`
    case ScheduledPostErrorType.MediaFormatError:
      return `${platformName} doesn't support this media format.`
    case ScheduledPostErrorType.RateLimitError:
      return `${platformName} rate limit exceeded. Try again later.`
    case ScheduledPostErrorType.NetworkError:
      return 'Network connection failed. Please check your internet connection.'
    default:
      return 'Post failed to upload'
  }
}
